import { CETUS, PositionConfigInfo, TypeArgument, USDC } from '@kunalabs-io/kai'
import styled from 'styled-components'
import { useMemo } from 'react'
import Decimal from 'decimal.js'

import { PositionInfo } from '@/shared/kai/hooks/useUserPositions'

import { Tooltip } from 'components/Tooltip/Tooltip'
import { IconInfo } from 'components/Icons/InfoIcon'
import { Typography } from 'components/Typography/Typography'
import { Banner } from 'components/Banner/Banner'
import { useIsMobile } from 'utils/useIsMobile'
import { ReversedWrapper } from 'components/ReversedWrapper/ReversedWrapper'
import { useCetusRedemptionInfo } from '../hooks/useCetusRedemptionInfo'
import { ContentLoading } from './ContentLoading'
import { useTokenPrice } from 'utils/useTokenPrice'
import { useRepayDebtInfo } from '../hooks/useRepayDebtInfo'
import { getFormattedUsdValue } from 'utils/usdFormatters'

interface Props {
  configInfo: PositionConfigInfo<string, string, TypeArgument>
  positionInfo: PositionInfo
  amountPercentage: number
  isReversedPair: boolean
  isCetusAffected: boolean
}

const Container = styled.div`
  display: flex;
  gap: 16px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    display: block;
  }
`

const SectionLabel = styled(Typography)`
  display: flex;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 16px;
  svg {
    display: flex;
    cursor: pointer;
    margin-left: 6px;
    path {
      stroke: ${p => p.theme.colors.text.secondary};
    }
  }
`

const PositionBreakdownContainer = styled.div`
  padding: 20px 16px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background.secondary};
`

const PositionBreakdownContainerItem = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
`

const PositionBreakdownSymbol = styled(Typography)`
  display: flex;
  svg {
    margin-right: 8px;
  }
`

const UsdEstimate = styled(Typography)`
  position: absolute;
  right: 0;
`

export const RepayDebtAndCetusRedemption = ({
  configInfo,
  positionInfo,
  amountPercentage,
  isReversedPair,
  isCetusAffected,
}: Props) => {
  const { isLoading: isLoadingCetusRedemption, cetusRedemptionAmount } = useCetusRedemptionInfo(
    positionInfo,
    isCetusAffected
  )
  const { additionalAmountTokenNum, errorNotEnoughTokens, repayDebtX, repayDebtY } = useRepayDebtInfo(
    configInfo,
    positionInfo,
    amountPercentage
  )

  const { isLoading: isLoadingCetusPrice, price: priceUsdcCetus } = useTokenPrice(CETUS, USDC)
  const { isLoading: isLoadingUsdValueX, price: priceX } = useTokenPrice(configInfo.X, USDC)
  const { isLoading: isLoadingUsdValueY, price: priceY } = useTokenPrice(configInfo.Y, USDC)

  const cetusValueUsdc = useMemo(() => {
    let cetusUsdcDecimal: Decimal | undefined = undefined
    if (!isLoadingCetusPrice && priceUsdcCetus && cetusRedemptionAmount) {
      cetusUsdcDecimal = new Decimal(0).plus(cetusRedemptionAmount.toDecimal().mul(priceUsdcCetus.human))
      return getFormattedUsdValue(cetusUsdcDecimal.toNumber())
    }
    return undefined
  }, [cetusRedemptionAmount, isLoadingCetusPrice, priceUsdcCetus])

  const { repayDebtXUsd, repayDebtYUsd } = useMemo(() => {
    let repayDebtXUsd = ''
    let repayDebtYUsd = ''
    let repayDebtXDecimal: Decimal | undefined = undefined
    let repayDebtYDecimal: Decimal | undefined = undefined

    if (priceX && !isLoadingUsdValueX && repayDebtX) {
      repayDebtXDecimal = new Decimal(0).plus(new Decimal(repayDebtX).mul(priceX.human))
      repayDebtXUsd = getFormattedUsdValue(repayDebtXDecimal.toNumber())
    }

    if (priceY && !isLoadingUsdValueY && repayDebtY) {
      repayDebtYDecimal = new Decimal(0).plus(new Decimal(repayDebtY).mul(priceY.human))
      repayDebtYUsd = getFormattedUsdValue(repayDebtYDecimal.toNumber())
    }

    return {
      repayDebtXUsd,
      repayDebtYUsd,
    }
  }, [isLoadingUsdValueX, isLoadingUsdValueY, priceX, priceY, repayDebtX, repayDebtY])

  const isMobile = useIsMobile()

  return (
    <>
      <Container>
        {additionalAmountTokenNum > 0 && (
          <div style={{ width: isMobile || !isCetusAffected ? '100%' : '50%' }}>
            <>
              <SectionLabel variant="body" color="secondary">
                Additional collateral needed
                <Tooltip
                  content="The amount of additional collateral needed to close your position."
                  position={isMobile ? 'bottom' : 'top'}
                  // tooltipBoxStyles={{ marginLeft: 54 }}
                  tooltipBoxStyles={isMobile ? { marginLeft: -80 } : {}}
                >
                  <IconInfo width={16} height={16} />
                </Tooltip>
              </SectionLabel>
              <PositionBreakdownContainer>
                {additionalAmountTokenNum === 2 ? (
                  <ReversedWrapper
                    childX={
                      <PositionBreakdownContainerItem style={isReversedPair ? {} : { marginBottom: 20 }}>
                        <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
                          <img src={configInfo?.X.iconUrl} alt={configInfo?.X.displaySymbol} width={24} height={24} />
                          &nbsp;
                          {configInfo?.X.displaySymbol}
                        </PositionBreakdownSymbol>
                        <Typography variant="description" fontWeight="semiBold">
                          {repayDebtX}
                          <UsdEstimate variant="caption" color="secondary">
                            {repayDebtXUsd}
                          </UsdEstimate>
                        </Typography>
                      </PositionBreakdownContainerItem>
                    }
                    childY={
                      <PositionBreakdownContainerItem style={isReversedPair ? { marginBottom: 20 } : {}}>
                        <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
                          <img src={configInfo?.Y.iconUrl} alt={configInfo?.Y.displaySymbol} width={24} height={24} />
                          &nbsp;{configInfo?.Y.displaySymbol}
                        </PositionBreakdownSymbol>
                        <Typography variant="description" fontWeight="semiBold">
                          {repayDebtY}
                          <UsdEstimate variant="caption" color="secondary">
                            {repayDebtYUsd}
                          </UsdEstimate>
                        </Typography>
                      </PositionBreakdownContainerItem>
                    }
                    isReversed={isReversedPair}
                  />
                ) : (
                  <PositionBreakdownContainerItem>
                    <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
                      {repayDebtX ? (
                        <>
                          <img src={configInfo?.X.iconUrl} alt={configInfo?.X.displaySymbol} width={24} height={24} />
                          &nbsp;
                          {configInfo?.X.displaySymbol}
                        </>
                      ) : (
                        <>
                          <img src={configInfo?.Y.iconUrl} alt={configInfo?.Y.displaySymbol} width={24} height={24} />
                          &nbsp;
                          {configInfo?.Y.displaySymbol}
                        </>
                      )}
                    </PositionBreakdownSymbol>
                    <Typography variant="description" fontWeight="semiBold">
                      {repayDebtX || repayDebtY}
                      <UsdEstimate variant="caption" color="secondary">
                        {repayDebtXUsd || repayDebtYUsd}
                      </UsdEstimate>
                    </Typography>
                  </PositionBreakdownContainerItem>
                )}
                <div style={{ height: 20 }} />
              </PositionBreakdownContainer>
            </>
          </div>
        )}
        {isCetusAffected && (
          <div style={{ width: isMobile || additionalAmountTokenNum === 0 ? '100%' : '50%' }}>
            <SectionLabel variant="body" color="secondary">
              Total Cetus compensation amount
              <Tooltip
                content="The amount of CETUS tokens you will receive as compensation through vesting after you close your position."
                position={isMobile ? 'bottom' : 'top'}
                tooltipBoxStyles={isMobile ? { marginLeft: -80 } : { minWidth: 300, marginLeft: -80 }}
              >
                <IconInfo width={16} height={16} />
              </Tooltip>
            </SectionLabel>
            <PositionBreakdownContainer>
              <PositionBreakdownContainerItem style={{ alignItems: 'center' }}>
                <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
                  <img src="/cetus.png" alt="CETUS" width={24} height={24} />
                  &nbsp; CETUS
                </PositionBreakdownSymbol>
                {isLoadingCetusRedemption ? (
                  <ContentLoading />
                ) : (
                  <Typography variant="description" fontWeight="semiBold">
                    {cetusRedemptionAmount?.toNumber()}
                    {cetusValueUsdc && (
                      <UsdEstimate variant="caption" color="secondary">
                        {cetusValueUsdc}
                      </UsdEstimate>
                    )}
                  </Typography>
                )}
              </PositionBreakdownContainerItem>
              <div style={{ height: 20 }} />
            </PositionBreakdownContainer>
          </div>
        )}
      </Container>
      {errorNotEnoughTokens && (
        <Banner style={{ marginTop: 16, width: 'auto' }} variant="error">
          <Typography variant={isMobile ? 'caption2' : 'body'} fontWeight="semiBold">
            There is not enough required collateral in your wallet to close your position. Transfer above amounts to
            your wallet and try again.
          </Typography>
        </Banner>
      )}
    </>
  )
}
